"use client";

import { useEffect, useState } from "react";

type Stats = {
  totalCustomers: number;
  totalLeads: number;
  totalCalls: number;
  pendingFollowUps: number;
  callsToday: number;
  leadsByStatus: { status: string; _count: number }[];
  recentCalls: {
    id: string;
    customer: { name: string; phone: string };
    status: string;
    direction: string;
    duration: number | null;
    createdAt: string;
    language: string;
  }[];
};

const STATUS_COLORS: Record<string, string> = {
  NEW: "bg-blue-100 text-blue-800",
  CONTACTED: "bg-yellow-100 text-yellow-800",
  QUALIFIED: "bg-purple-100 text-purple-800",
  PROPOSAL: "bg-orange-100 text-orange-800",
  NEGOTIATION: "bg-pink-100 text-pink-800",
  WON: "bg-green-100 text-green-800",
  LOST: "bg-red-100 text-red-800",
};

const CALL_STATUS_COLORS: Record<string, string> = {
  COMPLETED: "bg-green-100 text-green-800",
  IN_PROGRESS: "bg-blue-100 text-blue-800",
  FAILED: "bg-red-100 text-red-800",
  INITIATED: "bg-yellow-100 text-yellow-800",
  NO_ANSWER: "bg-gray-100 text-gray-800",
};

export default function DashboardPage() {
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch("/api/stats")
      .then((r) => r.json())
      .then((data) => {
        setStats(data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-pulse text-gray-400 text-lg">Loading dashboard...</div>
      </div>
    );
  }

  const statCards = [
    { label: "Total Customers", value: stats?.totalCustomers ?? 0, icon: "👥", color: "bg-blue-500" },
    { label: "Active Leads", value: stats?.totalLeads ?? 0, icon: "🎯", color: "bg-purple-500" },
    { label: "Total Calls", value: stats?.totalCalls ?? 0, icon: "📞", color: "bg-green-500" },
    { label: "Calls Today", value: stats?.callsToday ?? 0, icon: "📱", color: "bg-orange-500" },
    { label: "Pending Follow-ups", value: stats?.pendingFollowUps ?? 0, icon: "📅", color: "bg-red-500" },
  ];

  return (
    <div className="p-8">
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900">Dashboard</h2>
        <p className="text-gray-500 mt-1">AI Voice CRM Overview</p>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
        {statCards.map((card) => (
          <div key={card.label} className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
            <div className={`w-10 h-10 ${card.color} rounded-lg flex items-center justify-center text-xl mb-3`}>
              {card.icon}
            </div>
            <div className="text-2xl font-bold text-gray-900">{card.value}</div>
            <div className="text-sm text-gray-500 mt-0.5">{card.label}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Leads by Status */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <h3 className="font-semibold text-gray-900 mb-4">Leads Pipeline</h3>
          <div className="space-y-3">
            {stats?.leadsByStatus?.map((item) => (
              <div key={item.status} className="flex items-center justify-between">
                <span className={`text-xs font-medium px-2.5 py-0.5 rounded-full ${STATUS_COLORS[item.status] || "bg-gray-100 text-gray-800"}`}>
                  {item.status}
                </span>
                <div className="flex items-center gap-3">
                  <div className="w-32 bg-gray-100 rounded-full h-2">
                    <div
                      className="bg-indigo-500 h-2 rounded-full"
                      style={{
                        width: `${Math.min(100, (item._count / (stats.totalLeads || 1)) * 100)}%`,
                      }}
                    />
                  </div>
                  <span className="text-sm font-medium text-gray-700 w-6 text-right">{item._count}</span>
                </div>
              </div>
            ))}
            {(!stats?.leadsByStatus || stats.leadsByStatus.length === 0) && (
              <p className="text-sm text-gray-400">No leads yet</p>
            )}
          </div>
        </div>

        {/* Recent Calls */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <h3 className="font-semibold text-gray-900 mb-4">Recent Calls</h3>
          <div className="space-y-3">
            {stats?.recentCalls?.map((call) => (
              <div key={call.id} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
                <div>
                  <p className="text-sm font-medium text-gray-900">{call.customer.name}</p>
                  <p className="text-xs text-gray-400">{call.customer.phone} · {call.language}</p>
                </div>
                <div className="text-right">
                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${CALL_STATUS_COLORS[call.status] || "bg-gray-100 text-gray-800"}`}>
                    {call.status}
                  </span>
                  {call.duration && (
                    <p className="text-xs text-gray-400 mt-0.5">{Math.floor(call.duration / 60)}m {call.duration % 60}s</p>
                  )}
                </div>
              </div>
            ))}
            {(!stats?.recentCalls || stats.recentCalls.length === 0) && (
              <p className="text-sm text-gray-400">No calls yet</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
